import React, { useState, useEffect, useRef, useMemo, useReducer, useCallback } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { usePersistentStorage } from 'hooks/usePersistentStorage';
import { useUpdateState } from 'hooks/useUpdateState';
import { useStateNavigation } from 'hooks/useStateNavigation';
import { useI18n } from 'utils/i18n/usei18n';
import { AppState } from 'store/index';
import { LocationType } from 'types/LocationTypes';
import { Constants } from 'utils/Constants';
import cloneDeep from 'lodash/cloneDeep';
import { StationsSearchService, TO_STATION, FROM_STATION, lists } from './StationSearchService';
import {
  stationFormReducer,
  StationFormAction,
  StationFormValidationContext,
  validateStationForm,
} from './StationSearchForm';
import { StationSearchContainer } from './StationSearchContainer';
import { StationComboBox } from './StationComboBox';
import { StationInput } from './StationInput';
import { SearchResult } from './SearchResult';
import { SwapStations } from './SwapStations';
import { FlexColumn } from 'elements/containers/Containers';

type StationSearchProps = {
  small: string;
  medium: string;
  large: string;
  displaySwap: boolean;
  displaySubmit: boolean;
  redirectUrl?: string;
  onFormValid?: () => void;
};

type StyleProps = {
  isFocused?: boolean;
  hide?: boolean;
};

const FormGroupContainer = styled(FlexColumn)`
  ${(_: StyleProps) => ''}
  width: 100%;
  position: relative;
  /* Menu needs to be placed over search if search is not in focus*/
  z-index: ${(props) => (props.isFocused ? props.theme.newconstants.zIndexMedium : 0)};

  @media ${(props) => props.theme.breakpoints.large} {
    display: flex;
    flex-direction: row;
    border: 0;
    border-radius: 0;
  }
`;

const filterStations = (value: string, stations: LocationType[]): LocationType[] => {
  if (!value) return null;
  return stations
    .filter((station: LocationType) => station.name.toLowerCase().startsWith(value.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name))
    .splice(0, 3);
};

const getShortcuts = (earlierSearches: { from: string; to: string }[], popularStations: string[]): string[] => {
  if (!earlierSearches) return popularStations;
  const stations = [];
  earlierSearches.forEach((search) => {
    stations.push(search.from);
    stations.push(search.to);
  });
  return [...new Set(stations.concat(popularStations))].slice(0, 6);
};

export const SectionStationSearch = ({
  small,
  medium,
  large,
  displaySwap,
  displaySubmit,
  redirectUrl,
  onFormValid,
}: StationSearchProps): JSX.Element => {
  const { isFormValid } = StationsSearchService;
  const { getCachedItem, cacheItem } = usePersistentStorage();
  const { navigateWithState } = useStateNavigation();
  const { setOrderStations, deleteOrderStations, setDialog } = useUpdateState();
  const { translate } = useI18n();
  const { currentDialog } = useSelector((state: AppState) => state.global);
  const [form, dispatchForm] = useReducer(stationFormReducer, StationsSearchService.getForm());
  const [positionActive, setPositionActive] = useState(true);
  const [searchResult, setSearchResult] = useState(null);
  const [listFocusState, setListFocusState] = useState({ row: 0, list: null });
  const stations = useSelector((state: AppState) => state.locations);
  const order = useSelector((state: AppState) => state.order);
  const settings = getCachedItem(Constants.CACHE_SETTINGS);
  const earlierSearches = settings && settings[Constants.CACHE_SEARCHED_STATIONS];
  const shortcuts = getShortcuts(earlierSearches, Constants.POPULAR_STATIONS);
  const inputRef = useRef<(HTMLInputElement | null)[]>([]);
  const hasHydratedForm = useRef(false);
  const lastCommittedSearch = useRef<string>();
  const sameStationError = translate('SEARCH_COMPONENT_NO_SAME');
  const validationContext = useMemo<StationFormValidationContext>(
    () => ({
      stopPlaces: stations.stopPlaces,
      problemCombinationTexts: stations.stopPlaceProblemCombinationTexts,
      sameStationError,
    }),
    [sameStationError, stations.stopPlaces, stations.stopPlaceProblemCombinationTexts],
  );

  const resetListFocus = () => {
    setSearchResult(null);
    setListFocusState({ row: 0, list: earlierSearches ? lists.earlierSearches : lists.shortcuts });
  };

  const handleFieldFocus = (field: string): void => {
    const validatedForm = validateStationForm(form, validationContext);
    dispatchForm({ type: 'revalidate', context: validationContext });
    const focusedField = validatedForm.get(field);
    if (inputRef.current[field].value === '') {
      resetListFocus();
    } else {
      setSearchResult(filterStations(focusedField.value, stations.stopPlaces));
      setListFocusState({ row: 0, list: lists.searchResult });
    }
    setDialog({ name: field, color: 'transparent', scroll: true });
  };

  const handleFieldChange = (name: string, value: string): void => {
    if (value === '') resetListFocus();
    else {
      setSearchResult(filterStations(value, stations.stopPlaces));
      setListFocusState({ row: 0, list: lists.searchResult });
    }
    setOrderStations(undefined, undefined);
    setPositionActive(false);
    dispatchForm({ type: 'set-values', values: { [name]: value }, context: validationContext });
  };

  const handleBlur = (): void => {
    const validatedForm = validateStationForm(form, validationContext);
    const hasErrors = Array.from(validatedForm.values()).find(
      (field) => field.error || field.serviceMessage || (field.value !== '' && !field.stationExists),
    );
    if (hasErrors) {
      inputRef.current[hasErrors.name].focus();
    } else {
      resetListFocus();
      setDialog(undefined);
    }
    dispatchForm({ type: 'revalidate', context: validationContext });
  };

  const handleTab = (): void => {
    const validatedForm = validateStationForm(form, validationContext);
    const latest = validatedForm.get(currentDialog?.name);
    if (latest && (latest.isValid || latest.value === '')) {
      setDialog(undefined);
      resetListFocus();
    }
    dispatchForm({ type: 'revalidate', context: validationContext });
  };

  const handleClearField = (field: string): void => {
    inputRef.current[field].value = '';
    dispatchForm({ type: 'set-values', values: { [field]: '' }, context: validationContext });
    setPositionActive(false);
    deleteOrderStations();
    resetListFocus();

    inputRef.current[field].focus();
  };

  const handlePositionChange = (field: string, value: string): void => {
    if (value) {
      setPositionActive(true);
      handleSelect(field, value);
    } else {
      handleClearField(field);
    }
  };

  const handleSelect = (field: string, station: string, secondStation = null): void => {
    const action: StationFormAction = {
      type: 'set-values',
      values: secondStation ? { [FROM_STATION]: station, [TO_STATION]: secondStation } : { [field]: station },
      context: validationContext,
    };
    const validatedForm = stationFormReducer(form, action);
    const validatedFrom = validatedForm.get(FROM_STATION);
    const validatedTo = validatedForm.get(TO_STATION);
    const fromErrors = validatedFrom.error || validatedFrom.serviceMessage || !validatedFrom.stationExists;
    const toErrors = validatedTo.error || validatedTo.serviceMessage || !validatedTo.stationExists;

    if (field === FROM_STATION && !fromErrors && (!validatedTo.value || toErrors)) {
      inputRef.current[FROM_STATION].blur();
      inputRef.current[TO_STATION].focus();
    } else if (field === TO_STATION && !toErrors && fromErrors) {
      inputRef.current[TO_STATION].blur();
      inputRef.current[FROM_STATION].focus();
    } else if (field === FROM_STATION && fromErrors) {
      inputRef.current[FROM_STATION].focus();
    } else if (field === TO_STATION && toErrors) {
      inputRef.current[TO_STATION].focus();
    } else {
      if (inputRef) inputRef.current[field].blur();
      resetListFocus();
      setDialog(undefined);
    }
    dispatchForm(action);
  };

  const cacheSearch = useCallback(
    (locationFrom: string, locationTo: string): void => {
      const tempSearches = earlierSearches ? Array.from(earlierSearches) : [];
      const index = tempSearches.findIndex((search) => search['from'] === locationFrom && search['to'] === locationTo);
      if (index >= 0) tempSearches.splice(index, 1);
      tempSearches.unshift({ from: locationFrom, to: locationTo });
      const tempSettings = settings ? cloneDeep(settings) : {};
      tempSettings[Constants.CACHE_SEARCHED_STATIONS] = tempSearches.slice(0, 3);
      cacheItem(Constants.CACHE_SETTINGS, tempSettings);
    },
    [cacheItem, earlierSearches, settings],
  );

  const handleSwapStations = (): void => {
    dispatchForm({ type: 'swap', context: validationContext });
  };

  const handleSubmit = () => {
    const hasErrors = Array.from(form.values()).find((field) => !field.isValid);
    if (hasErrors) inputRef.current[hasErrors.name].focus();
    else navigateWithState(redirectUrl);
  };

  useEffect(() => {
    if (hasHydratedForm.current || (!order.from && !order.to)) return;

    hasHydratedForm.current = true;
    dispatchForm({ type: 'hydrate', order, context: validationContext });
  }, [order, validationContext]);

  useEffect(() => {
    dispatchForm({ type: 'revalidate', context: validationContext });
  }, [validationContext]);

  useEffect(() => {
    const fromField = form.get(FROM_STATION);
    const toField = form.get(TO_STATION);
    if (!fromField.isValid || !toField.isValid) {
      lastCommittedSearch.current = undefined;
      return;
    }

    const locationFrom = stations.stopPlaces.find(
      (station) => station.name.toLowerCase() === fromField.value.trim().toLowerCase(),
    );
    const locationTo = stations.stopPlaces.find(
      (station) => station.name.toLowerCase() === toField.value.trim().toLowerCase(),
    );
    if (!locationFrom || !locationTo) return;

    const searchKey = `${locationFrom.id}:${locationTo.id}`;
    if (lastCommittedSearch.current === searchKey) return;

    lastCommittedSearch.current = searchKey;
    setOrderStations(locationFrom, locationTo);
    cacheSearch(locationFrom.name, locationTo.name);
  }, [cacheSearch, form, setOrderStations, stations.stopPlaces]);

  const hasSelectedStations = Boolean(order.from && order.to);
  useEffect(() => {
    if (hasSelectedStations) onFormValid?.();
  }, [hasSelectedStations, onFormValid]);

  return (
    form && (
      <form noValidate autoComplete="off" role="search" aria-label={translate('LABEL_SEARCH_DESTINATIONS')}>
        <StationSearchContainer
          small={small}
          medium={medium}
          large={large}
          fromValue={form.get(FROM_STATION).value !== '' || currentDialog?.name === FROM_STATION}
          toValue={form.get(TO_STATION).value !== '' || currentDialog?.name === TO_STATION}
          displaySubmit={displaySubmit}
          onSubmit={handleSubmit}
        >
          <FormGroupContainer
            id="stationFormGroup"
            role="form-group"
            aria-label={translate('LABEL_CHOOSE_DESTINATIONS')}
            isFocused={currentDialog?.name === 'toStation' || currentDialog?.name === 'fromStation'}
          >
            {Array.from(form.values()).map((field, index) => (
              <React.Fragment key={index}>
                <StationComboBox
                  owns={`${field.name}ListBox`}
                  expanded={currentDialog?.name === field.name}
                  fieldInFocus={currentDialog?.name}
                  searchResult={searchResult}
                  earlierSearches={earlierSearches}
                  shortcuts={shortcuts}
                  listFocusState={listFocusState}
                  onUpdateFocusState={({ row, list }) => setListFocusState({ row, list })}
                  onTab={handleTab}
                  onBlur={handleBlur}
                  onSelect={handleSelect}
                >
                  <StationInput
                    ref={(ref) => (inputRef.current[field.name] = ref)}
                    field={field}
                    positionActive={positionActive}
                    fieldInFocus={currentDialog?.name === field.name}
                    neighbourInFocus={
                      (field.name === 'fromStation' && currentDialog?.name === 'toStation') ||
                      (field.name === 'toStation' && currentDialog?.name === 'fromStation')
                    }
                    displaySwopStations={displaySwap}
                    stopPlaces={Boolean(stations.stopPlaces.length)}
                    activeDecendant={`${listFocusState.list}${listFocusState.row}`}
                    onFieldFocus={(field: string) => handleFieldFocus(field)}
                    onFieldChange={handleFieldChange}
                    onClearField={handleClearField}
                    onBlurField={handleBlur}
                    onPositionChange={handlePositionChange}
                  />

                  {currentDialog?.name === field.name && (
                    <SearchResult
                      field={field}
                      listFocusState={listFocusState}
                      resultList={searchResult}
                      shortcuts={shortcuts}
                      earlierSearches={earlierSearches}
                      stopPlaces={Boolean(stations.stopPlaces.length)}
                      onSelect={handleSelect}
                    />
                  )}
                </StationComboBox>

                {field.left && displaySwap && (
                  <SwapStations isFormValid={isFormValid(form)} onSwapStations={handleSwapStations} />
                )}
              </React.Fragment>
            ))}
          </FormGroupContainer>
        </StationSearchContainer>
      </form>
    )
  );
};
